import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Languages as LangIcon, ArrowLeft, Search, Tv } from "lucide-react";

const API = "/api";

function LangCard({ l, fr }) {
  return (
    <Link to={`/languages/${l.slug}`} data-testid={`lang-card-${l.slug}`}
      className="group block rounded-2xl overflow-hidden border border-white/[0.06] bg-[#0F172A] hover:border-[#FFC928]/30 transition-all">
      <div className="h-28 bg-gradient-to-br from-[#1e293b] to-[#0b1220] flex items-center justify-center relative">
        {l.image_url ? <img src={l.image_url} alt="" className="w-full h-full object-cover" /> : <LangIcon size={34} className="text-[#FFC928]/60" />}
      </div>
      <div className="p-4">
        <h3 className="text-white font-semibold">{fr ? l.name_fr : l.name_en}</h3>
        <p className="text-[#94A3B8] text-xs mt-0.5">{l.native_name}</p>
        <p className="text-[#FFC928] text-xs mt-2">{l.channel_count} {fr ? "chaînes" : "channels"}</p>
      </div>
    </Link>
  );
}

export default function PublicLanguages() {
  const { slug } = useParams();
  const { i18n } = useTranslation();
  const fr = (i18n.language || "en").startsWith("fr");
  const navigate = useNavigate();
  const [langs, setLangs] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    setLoading(true);
    if (slug) {
      const p = new URLSearchParams({ search });
      axios.get(`${API}/languages/${slug}?${p}`).then(r => { setDetail(r.data); setLoading(false); }).catch(() => { setDetail(null); setLoading(false); });
    } else {
      axios.get(`${API}/languages`).then(r => { setLangs(r.data || []); setLoading(false); }).catch(() => setLoading(false));
    }
  }, [slug, search]);

  if (loading) return <div className="min-h-screen bg-[#050814] flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#FFC928] border-t-transparent rounded-full animate-spin" /></div>;

  // Detail page
  if (slug) {
    if (!detail) return <div className="min-h-screen bg-[#050814] text-center text-white py-20" data-testid="lang-not-found">{fr ? "Langue introuvable" : "Language not found"}</div>;
    const l = detail.language;
    return (
      <div className="min-h-screen bg-[#050814] text-white" data-testid="language-detail">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <button onClick={() => navigate("/languages")} className="flex items-center gap-1 text-sm text-[#94A3B8] hover:text-white mb-4"><ArrowLeft size={16} /> {fr ? "Toutes les langues" : "All languages"}</button>
          <div className="flex items-center gap-3 mb-6">
            <LangIcon size={28} className="text-[#FFC928]" />
            <div>
              <h1 className="text-3xl font-bold" style={{ fontFamily: "Poppins" }}>{fr ? l.name_fr : l.name_en}</h1>
              <p className="text-[#94A3B8] text-sm">{l.native_name} · {detail.total} {fr ? "chaînes" : "channels"}</p>
            </div>
          </div>
          <div className="relative max-w-sm mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" size={16} />
            <input value={search} onChange={e => setSearch(e.target.value)} data-testid="lang-detail-search" placeholder={fr ? "Rechercher..." : "Search..."} className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-white/[0.05] border border-white/[0.08] text-white text-sm" />
          </div>
          {detail.channels.length === 0 ? (
            <div className="text-center py-16 text-[#94A3B8]" data-testid="lang-detail-empty">{fr ? "Aucune chaîne" : "No channels"}</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="lang-channel-grid">
              {detail.channels.map(ch => (
                <Link key={ch.id} to={`/watch/${ch.id}`} className="group rounded-xl overflow-hidden border border-white/[0.06] bg-[#0F172A] hover:border-[#FFC928]/30 transition-all">
                  <div className="aspect-video bg-[#1e293b] flex items-center justify-center">
                    {ch.logo_url ? <img src={ch.logo_url} alt="" className="w-full h-full object-contain p-2" /> : <Tv size={24} className="text-[#94A3B8]" />}
                  </div>
                  <div className="p-2.5"><p className="text-xs font-medium text-white truncate">{ch.name}</p></div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // List page
  return (
    <div className="min-h-screen bg-[#050814] text-white" data-testid="languages-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center gap-3 mb-8">
          <LangIcon size={28} className="text-[#FFC928]" />
          <h1 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: "Poppins" }}>{fr ? "Langues" : "Languages"}</h1>
        </div>
        {langs.length === 0 ? (
          <div className="text-center py-16 text-[#94A3B8]" data-testid="languages-empty">{fr ? "Aucune langue disponible" : "No languages available yet"}</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {langs.map(l => <LangCard key={l.id} l={l} fr={fr} />)}
          </div>
        )}
      </div>
    </div>
  );
}
